import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { collection, query, orderBy, limit, getDocs, where, Timestamp, doc, getDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase-config';
import {
  Box, Typography, Paper, Button, List, ListItem,
  ListItemText, Divider, CircularProgress, Accordion,
  AccordionSummary, AccordionDetails, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogContentText,
  DialogActions, Menu, MenuItem, Tooltip
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import MoreVertIcon from '@mui/icons-material/MoreVert';

const DebugConversations = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedConversation, setSelectedConversation] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Get today's date at midnight
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      console.log('Fetching logs from today:', today);

      // 1. Fetch recent logs from generic_sms_logs
      const logsRef = collection(db, 'generic_sms_logs');
      const logsQuery = query(
        logsRef,
        where('timestamp', '>=', today),
        orderBy('timestamp', 'desc'),
        limit(20)
      );

      const logsSnapshot = await getDocs(logsQuery);

      const logsData = logsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate?.() || new Date(doc.data().timestamp)
      }));

      console.log(`Found ${logsData.length} logs from today`);
      setLogs(logsData);

      // 2. Fetch conversations from business subcollection
      const conversationsRef = collection(db, 'businesses', currentUser.uid, 'conversations');
      const conversationsQuery = query(
        conversationsRef,
        orderBy('createdAt', 'desc'),
        limit(50)
      );

      const conversationsSnapshot = await getDocs(conversationsQuery);

      const conversationsData = conversationsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt),
        updatedAt: doc.data().updatedAt?.toDate?.() || new Date(doc.data().updatedAt)
      }));

      console.log(`Found ${conversationsData.length} conversations from today`);
      setConversations(conversationsData);

      // 3. Try to directly fetch the specific conversation we know exists
      try {
        const specificConvId = 'gsl92LYLj0HPgGCNhw64';
        console.log(`Directly checking for conversation: ${specificConvId}`);

        // Try in business subcollection
        const specificConvRef = doc(db, 'businesses', currentUser.uid, 'conversations', specificConvId);
        const specificConvDoc = await getDoc(specificConvRef);

        if (specificConvDoc.exists()) {
          console.log(`Found specific conversation: ${specificConvId}`);
          const convData = specificConvDoc.data();
          console.log('Conversation data:', convData);

          // Add this conversation to our list if it's not already there
          const alreadyExists = conversations.some(conv => conv.id === specificConvId);
          if (!alreadyExists) {
            const formattedConv = {
              id: specificConvId,
              ...convData,
              createdAt: convData.createdAt?.toDate?.() || new Date(convData.createdAt),
              updatedAt: convData.updatedAt?.toDate?.() || new Date(convData.updatedAt)
            };
            setConversations(prev => [...prev, formattedConv]);
          }
        } else {
          console.log(`Specific conversation ${specificConvId} not found`);

          // Try in the top-level collection
          const topLevelSpecificRef = doc(db, 'conversations', specificConvId);
          const topLevelSpecificDoc = await getDoc(topLevelSpecificRef);

          if (topLevelSpecificDoc.exists()) {
            console.log(`Found specific conversation in top-level collection`);
            const convData = topLevelSpecificDoc.data();
            console.log('Conversation data:', convData);

            // Add this conversation to our list if it's not already there
            const formattedConv = {
              id: specificConvId,
              ...convData,
              createdAt: convData.createdAt?.toDate?.() || new Date(convData.createdAt),
              updatedAt: convData.updatedAt?.toDate?.() || new Date(convData.updatedAt)
            };
            setConversations(prev => [...prev, formattedConv]);
          }
        }

        // Also list all conversations in the business subcollection
        const allConvsRef = collection(db, 'businesses', currentUser.uid, 'conversations');
        const allConvsQuery = query(allConvsRef);
        const allConvsSnapshot = await getDocs(allConvsQuery);

        console.log(`Found ${allConvsSnapshot.size} total conversations in business subcollection`);

        // Log all conversation IDs to help debug
        allConvsSnapshot.docs.forEach(doc => {
          console.log(`- Conversation ID: ${doc.id}`);
        });
      } catch (specificError) {
        console.error('Error fetching specific conversation:', specificError);
      }

    } catch (err) {
      console.error('Error fetching debug data:', err);
      setError('Failed to load data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle opening the menu
  const handleMenuOpen = (event, conversation) => {
    // Stop the event from propagating to prevent the accordion from toggling
    event.stopPropagation();
    console.log('Opening menu for conversation:', conversation.id);
    setMenuAnchorEl(event.currentTarget);
    setSelectedConversation(conversation);
  };

  // Handle closing the menu
  const handleMenuClose = () => {
    console.log('Closing menu');
    setMenuAnchorEl(null);
  };

  // Handle opening the delete confirmation dialog
  const handleDeleteClick = () => {
    setConversationToDelete(selectedConversation);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  // Handle closing the delete confirmation dialog
  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setConversationToDelete(null);
  };

  // Handle confirming the delete operation
  const handleDeleteConfirm = async () => {
    if (!conversationToDelete) return;

    setLoading(true);
    try {
      // Delete from business subcollection
      await deleteDoc(doc(db, 'businesses', currentUser.uid, 'conversations', conversationToDelete.id));

      // Update the state to remove the deleted conversation
      setConversations(prev => prev.filter(conv => conv.id !== conversationToDelete.id));

      console.log(`Conversation ${conversationToDelete.id} deleted successfully`);
    } catch (err) {
      console.error('Error deleting conversation:', err);
      setError(`Failed to delete conversation: ${err.message}`);
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setConversationToDelete(null);
    }
  };

  // Handle exporting a conversation
  const handleExport = (format) => {
    if (!selectedConversation) return;

    try {
      let dataStr;
      let fileName;

      if (format === 'json') {
        // Export as JSON
        const dataObj = {
          id: selectedConversation.id,
          customerNumber: selectedConversation.customerNumber,
          createdAt: selectedConversation.createdAt.toISOString(),
          updatedAt: selectedConversation.updatedAt.toISOString(),
          messages: selectedConversation.messages || []
        };

        dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(dataObj, null, 2));
        fileName = `conversation_${selectedConversation.id}.json`;
      } else {
        // Export as CSV
        const headers = "Timestamp,Role,Content\n";
        const rows = (selectedConversation.messages || []).map(msg => {
          const timestamp = msg.timestamp instanceof Date
            ? msg.timestamp.toISOString()
            : new Date(msg.timestamp?.seconds * 1000 || Date.now()).toISOString();

          return `"${timestamp}","${msg.role}","${msg.content.replace(/"/g, '""')}"`;
        }).join("\n");

        dataStr = "data:text/csv;charset=utf-8," + encodeURIComponent(headers + rows);
        fileName = `conversation_${selectedConversation.id}.csv`;
      }

      // Create a download link and trigger the download
      const downloadAnchorNode = document.createElement('a');
      downloadAnchorNode.setAttribute("href", dataStr);
      downloadAnchorNode.setAttribute("download", fileName);
      document.body.appendChild(downloadAnchorNode);
      downloadAnchorNode.click();
      downloadAnchorNode.remove();

      handleMenuClose();
    } catch (err) {
      console.error('Error exporting conversation:', err);
      setError(`Failed to export conversation: ${err.message}`);
      handleMenuClose();
    }
  };

  useEffect(() => {
    if (currentUser) {
      fetchData();
    }
  }, [currentUser]);

  return (
    <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h6">Conversations</Typography>
          <Typography variant="caption" color="textSecondary">v1.1 - Shows all messages in conversations</Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchData}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <Box>

          <Box>
            {conversations.length > 0 ? conversations.map((conv, index) => (
              <Accordion key={conv.id} sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Typography sx={{ fontWeight: 'bold', flexGrow: 1 }}>
                      {conv.customerNumber || 'Unknown Customer'}
                    </Typography>
                    <Chip
                      label={conv.id === 'gsl92LYLj0HPgGCNhw64' ? 'Today\'s Test' : conv.id}
                      size="small"
                      color={conv.id === 'gsl92LYLj0HPgGCNhw64' ? 'primary' : 'default'}
                    />
                    <Chip
                      label={`${conv.messages?.length || 0} messages`}
                      size="small"
                      color={conv.messages?.length > 0 ? 'success' : 'default'}
                    />
                    <Tooltip title="Options">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMenuOpen(e, conv);
                        }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Conversation Details:</Typography>
                    <Typography variant="body2">ID: {conv.id}</Typography>
                    <Typography variant="body2">Customer: {conv.customerNumber || 'N/A'}</Typography>
                    <Typography variant="body2">Created: {conv.createdAt.toLocaleString()}</Typography>
                    <Typography variant="body2">Updated: {conv.updatedAt.toLocaleString()}</Typography>
                    <Typography variant="body2">Status: {conv.status || 'N/A'}</Typography>
                  </Box>

                  {conv.messages && conv.messages.length > 0 ? (
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Messages ({conv.messages.length}):
                      </Typography>
                      <List sx={{ bgcolor: '#f5f5f5', borderRadius: 1 }}>
                        {conv.messages.map((msg, msgIndex) => (
                          <React.Fragment key={msgIndex}>
                            <ListItem alignItems="flex-start" sx={{
                              bgcolor: msg.role === 'assistant' ? 'rgba(0, 150, 136, 0.1)' : 'rgba(63, 81, 181, 0.1)',
                              borderRadius: 1,
                              mb: 1
                            }}>
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                      {msg.role === 'assistant' ? 'Business' : 'Customer'}
                                    </Typography>
                                    <Typography variant="caption">
                                      {msg.timestamp instanceof Date
                                        ? msg.timestamp.toLocaleString()
                                        : new Date(msg.timestamp?.seconds * 1000 || Date.now()).toLocaleString()}
                                    </Typography>
                                  </Box>
                                }
                                secondary={
                                  <Typography
                                    component="div"
                                    variant="body2"
                                    sx={{ mt: 1, whiteSpace: 'pre-wrap' }}
                                  >
                                    {msg.content || '(No content)'}
                                  </Typography>
                                }
                              />
                            </ListItem>
                          </React.Fragment>
                        ))}
                      </List>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No messages in this conversation
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            )) : (
              <Typography variant="body1" sx={{ p: 2, textAlign: 'center' }}>
                No conversations found
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {/* Options Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            elevation: 3,
            sx: {
              minWidth: '200px',
              mt: 1
            }
          }
        }}
      >
        <MenuItem onClick={() => handleExport('json')}>
          <DownloadIcon fontSize="small" sx={{ mr: 1 }} />
          Export as JSON
        </MenuItem>
        <MenuItem onClick={() => handleExport('csv')}>
          <DownloadIcon fontSize="small" sx={{ mr: 1 }} />
          Export as CSV
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete Conversation
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Delete Conversation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this conversation with {conversationToDelete?.customerNumber || 'Unknown Customer'}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default DebugConversations;
